import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Statistic,
  List,
  Badge,
  Progress,
  Carousel,
} from "antd";
import {
  CoffeeOutlined,
  TrophyOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  BarChartOutlined,
} from "@ant-design/icons";

import {
  useStatusQuery,
  useTilesDataQuery,
  useTodaysOrdersQuery,
  useOrdersByDateQuery,
} from "../../Store/Apis/bookingApi";

const MobileDashboard = () => {
  const navigate = useNavigate();

  // Mock data for top beverages (keep as fallback until we have product-level data)
  const [dashboardData, setDashboardData] = useState({
    topBeverages: [
      { name: "Cappuccino", orders: 45, revenue: 540.0, trend: "+12%" },
      { name: "Iced Coffee", orders: 38, revenue: 456.0, trend: "+8%" },
      { name: "Latte", orders: 32, revenue: 448.0, trend: "+5%" },
      { name: "Espresso", orders: 28, revenue: 252.0, trend: "-2%" },
      { name: "Americano", orders: 24, revenue: 288.0, trend: "+15%" },
    ],
    hourlyData: [
      { hour: "9AM", orders: 8 },
      { hour: "10AM", orders: 15 },
      { hour: "11AM", orders: 22 },
      { hour: "12PM", orders: 35 },
      { hour: "1PM", orders: 28 },
      { hour: "2PM", orders: 18 },
      { hour: "3PM", orders: 12 },
      { hour: "4PM", orders: 18 },
    ],
    recentOrders: [
      {
        id: "#001",
        customer: "Table 12",
        items: "Cappuccino x2, Croissant",
        time: "2 min ago",
        status: "preparing",
      },
      {
        id: "#002",
        customer: "Jane D.",
        items: "Iced Latte, Muffin",
        time: "5 min ago",
        status: "ready",
      },
      {
        id: "#003",
        customer: "Room 205",
        items: "Espresso x3",
        time: "8 min ago",
        status: "delivered",
      },
      {
        id: "#004",
        customer: "Mike R.",
        items: "Americano, Cookie",
        time: "12 min ago",
        status: "preparing",
      },
    ],
  });

  const colors = {
    primary: "#084c7c",
    success: "#52c41a",
    warning: "#b99028",
    white: "#ffffff",
    light: "#f8f9fa",
    text: "#333333",
  };

  const [sortFilter, setSortFilter] = useState({
    search: "",
    outlet: "",
    status: "",
    technicianID: "",
  });

  const todaysOrders = useTodaysOrdersQuery({
    keyword: sortFilter.search,
    statusName: sortFilter.status,
    technicianID: sortFilter.technicianID,
  });
  console.log("Historical order data:", todaysOrders.data);

  // Map and filter data to show only "Bar sales" orders
  const allOrders =
    todaysOrders?.data?.data?.map((elem, index) => ({
      key: index,
      orderNumber: elem.orderNumber,
      orderDate: elem.orderDate,
      outletName: elem.outletName,
      customerName: elem.customerName,
      customerNumber: elem.customerNumber,

      subTotal: Number(elem.subTotal + elem.taxAmount - elem.discount).toFixed(
        2
      ),
      statusDescription: elem.statusDescription,
      amountPaid: Number(elem.amountPaid).toFixed(2),
      totalAmount: Number(elem.totalAmount).toFixed(2),
      orderId: elem.orderId,
      createdBy: elem.createdBy,
      items: (() => {
        if (!elem.serviceTypeAndName) return "Bar Items";
        if (Array.isArray(elem.serviceTypeAndName)) {
          return elem.serviceTypeAndName
            .map(
              (item) => item.serviceName || item.productServiceName || "Item"
            )
            .join(", ");
        }
        if (typeof elem.serviceTypeAndName === "object") {
          return (
            elem.serviceTypeAndName.serviceName ||
            elem.serviceTypeAndName.productServiceName ||
            "Bar Item"
          );
        }
        return elem.serviceTypeAndName.toString();
      })(), // Convert serviceTypeAndName to string for display
      time: new Date(elem.orderDate).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
      status:
        elem.statusDescription?.toLowerCase().replace(/\s+/g, "") || "unknown",
    })) || [];

  // Filter for only "Bar sales" orders
  const dataSource = allOrders.filter(
    (order) => order.statusDescription === "Bar sales"
  );

  console.log("Filtered Bar Sales orders:", dataSource);

  // Calculate drink statistics from bar sales data
  const drinkStats = {};

  dataSource?.forEach((order) => {
    // Get the original order data to access serviceTypeAndName with quantities and rates
    const originalOrder = todaysOrders?.data?.data?.find(
      (o) => o.orderId === order.orderId
    );

    if (
      originalOrder?.serviceTypeAndName &&
      Array.isArray(originalOrder.serviceTypeAndName)
    ) {
      originalOrder.serviceTypeAndName.forEach((item) => {
        const drinkName =
          item.serviceName || item.productServiceName || "Unknown Drink";
        const quantity = parseInt(item.quantity) || 1;
        const rate = parseFloat(item.rate) || 0;
        const itemRevenue = quantity * rate;

        if (!drinkStats[drinkName]) {
          drinkStats[drinkName] = {
            name: drinkName,
            orders: 0,
            totalQuantity: 0,
            revenue: 0,
          };
        }

        drinkStats[drinkName].orders += 1;
        drinkStats[drinkName].totalQuantity += quantity;
        drinkStats[drinkName].revenue += itemRevenue;
      });
    }
  });

  // Convert to array and sort by total quantity (most ordered)
  const topBeverages = Object.values(drinkStats)
    .sort((a, b) => b.totalQuantity - a.totalQuantity)
    .slice(0, 5) // Top 5 drinks
    .map((drink, index) => ({
      ...drink,
      trend:
        index === 0
          ? "+15%"
          : index === 1
          ? "+8%"
          : index === 2
          ? "+5%"
          : "+2%", // Mock trend for now
    }));

  console.log("Top beverages calculated:", topBeverages);

  // Calculate real stats from bar sales data
  const todayStats = {
    totalOrders: dataSource?.length || 0,
    totalRevenue:
      dataSource?.reduce(
        (sum, order) => sum + parseFloat(order.amountPaid || 0),
        0
      ) || 0,
    averageOrderValue: dataSource?.length
      ? dataSource.reduce(
          (sum, order) => sum + parseFloat(order.amountPaid || 0),
          0
        ) / dataSource.length
      : 0,
    activeOrders:
      dataSource?.filter(
        (order) => order.status !== "delivered" && order.status !== "completed"
      ).length || 0,
  };

  const StatCard = ({ title, value, icon, color, subtitle, textColor }) => (
    <Card
      style={{
        borderRadius: "12px",
        border: "none",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        background: color,
        color: textColor || colors.white,
        margin: "8px 0",
      }}
      bodyStyle={{ padding: "16px" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "14px",
              opacity: 0.9,
              marginBottom: "4px",
              color: textColor || colors.white,
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              marginBottom: "4px",
              color: textColor || colors.white,
            }}
          >
            {value}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: "12px",
                opacity: 0.8,
                color: textColor || colors.white,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        <div
          style={{
            fontSize: "28px",
            opacity: 0.8,
            color: textColor || colors.white,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "preparing":
        return colors.warning;
      case "ready":
        return colors.success;
      case "delivered":
        return colors.primary;
      default:
        return colors.text;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "preparing":
        return "Preparing";
      case "ready":
        return "Ready";
      case "delivered":
        return "Delivered";
      default:
        return status;
    }
  };

  return (
    <div
      style={{
        padding: "16px",
        background: colors.light,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div
        style={{
          marginBottom: "20px",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            color: colors.primary,
            margin: 0,
            fontSize: "24px",
            fontWeight: "bold",
          }}
        >
          Drinks Dashboard
        </h2>
        <p
          style={{
            color: colors.text,
            margin: "4px 0 0 0",
            fontSize: "14px",
          }}
        >
          Today's Overview -{" "}
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>
      </div>

      {/* Stats Cards */}
      <Row gutter={[8, 8]} style={{ marginBottom: "20px" }}>
        <Col span={12}>
          <StatCard
            title="Total Orders"
            value={todayStats.totalOrders}
            icon={<ShoppingCartOutlined />}
            color={colors.white}
            subtitle="Bar Sales Today"
            textColor={colors.primary}
          />
        </Col>
        <Col span={12}>
          <StatCard
            title="Revenue"
            value={`R${todayStats.totalRevenue.toFixed(2)}`}
            icon={<RiseOutlined />}
            color={colors.success}
            subtitle="Bar Sales Total"
          />
        </Col>
        <Col span={12}>
          <StatCard
            title="Active Orders"
            value={todayStats.activeOrders}
            icon={<ClockCircleOutlined />}
            color={colors.warning}
            subtitle="In Progress"
          />
        </Col>
        <Col span={12}>
          <StatCard
            title="Avg Order"
            value={`R${todayStats.averageOrderValue.toFixed(2)}`}
            icon={<BarChartOutlined />}
            color={colors.primary}
            subtitle="Per Order"
          />
        </Col>
      </Row>

      {/* Top Beverages Carousel */}
      <Card
        title={
          <div
            style={{
              color: colors.primary,
              display: "flex",
              alignItems: "center",
            }}
          >
            <TrophyOutlined style={{ marginRight: "8px" }} />
            Top Beverages Today
          </div>
        }
        style={{
          marginBottom: "20px",
          borderRadius: "12px",
          border: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
        bodyStyle={{ padding: "16px" }}
      >
        <Carousel
          dots={true}
          dotPosition="bottom"
          autoplay={false}
          style={{
            "--carousel-dot-active-color": colors.primary,
            "--carousel-dot-color": "#e90f0fff",
          }}
        >
          {topBeverages && topBeverages.length > 0 ? (
            topBeverages.map((item, index) => (
              <div key={index}>
                <div
                  style={{
                    padding: "20px",
                    background: "#084c7c",
                    borderRadius: "8px",
                    margin: "0 4px",
                    textAlign: "center",
                    minHeight: "120px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <Badge
                      count={index + 1}
                      style={{
                        backgroundColor:
                          index === 0 ? colors.warning : colors.primary,
                        marginRight: "8px",
                      }}
                    />
                    <span
                      style={{
                        fontWeight: "600",
                        color: "white",
                        fontSize: "16px",
                      }}
                    >
                      {item.name}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "14px",
                      color: "#ffffffff",
                      marginBottom: "8px",
                    }}
                  >
                    <span>{item.totalQuantity} sold</span>
                    <span>R{item.revenue.toFixed(2)}</span>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                    }}
                  >
                    <span
                      style={{
                        color: item.trend.startsWith("+")
                          ? colors.success
                          : "#ff4d4f",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      {item.trend}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div>
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "#999",
                  minHeight: "120px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <TrophyOutlined
                  style={{ fontSize: "48px", marginBottom: "16px" }}
                />
                <div>No beverage data available</div>
                <div style={{ fontSize: "12px", marginTop: "8px" }}>
                  Bar sales will appear here
                </div>
              </div>
            </div>
          )}
        </Carousel>
      </Card>

      {/* Recent Orders Carousel */}
      <Card
        title={
          <div
            style={{
              color: colors.primary,
              display: "flex",
              alignItems: "center",
            }}
          >
            <CoffeeOutlined style={{ marginRight: "8px" }} />
            Recent Orders
          </div>
        }
        style={{
          marginBottom: "20px",
          borderRadius: "12px",
          border: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
        bodyStyle={{ padding: "16px" }}
      >
        {dataSource && dataSource.length > 0 ? (
          <Carousel dots={true} dotPosition="bottom" autoplay={false}>
            {dataSource.slice(0, 10).map(
              (
                order,
                index // Show latest 10 orders
              ) => (
                <div key={index}>
                  <div
                    style={{
                      padding: "20px",
                      background: "#084c7c",
                      borderRadius: "8px",
                      margin: "0 4px",
                      minHeight: "140px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "600",
                          color: "white",
                          fontSize: "16px",
                        }}
                      >
                        #{order.orderNumber} - {order.customerName || "Walk-in"}
                      </span>
                      <Badge
                        color={getStatusColor(order.status)}
                        text={order.statusDescription}
                        style={{ fontSize: "12px", color: "white" }}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#b99028",
                        marginBottom: "8px",
                        textAlign: "center",
                      }}
                    >
                      {order.items}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#52c41a",
                        textAlign: "center",
                        marginBottom: "8px",
                      }}
                    >
                      Total: R{order.subTotal}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#fffefeff",
                        textAlign: "center",
                      }}
                    >
                      {order.time}
                    </div>
                  </div>
                </div>
              )
            )}
          </Carousel>
        ) : (
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "#ffffffff",
            }}
          >
            <CoffeeOutlined
              style={{ fontSize: "48px", marginBottom: "16px" }}
            />
            <div>No bar sales orders today</div>
            <div style={{ fontSize: "12px", marginTop: "8px" }}>
              New orders will appear here
            </div>
          </div>
        )}
      </Card>

      {/* Quick Actions */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          marginTop: "20px",
        }}
      >
        {/* <button style={{
          flex: 1,
          padding: '12px',
          borderRadius: '8px',
          border: 'none',
          background: colors.primary,
          color: colors.white,
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer'
        }}>
          View All Orders
        </button> */}
        <button
          style={{
            flex: 1,
            padding: "12px",
            borderRadius: "8px",
            border: "none",
            background: colors.success,
            color: colors.white,
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
          }}
          onClick={() => navigate("/beverage-sales/bar-sale")}
        >
          New Order
        </button>
      </div>
    </div>
  );
};

export default MobileDashboard;
